import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { ShieldAlert, Loader2, CheckSquare } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// Shared one-click PII / classification consistency sweep for a single model.
// Used from the semantic model editor, the Data Catalog and the PII Compliance
// admin page so stewards can fix half-flagged columns from anywhere.
export function PiiConsistencyDialog({ modelId, modelName, open, onOpenChange, onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [choices, setChoices] = useState({}); // column -> winning category

  const load = useCallback(async () => {
    if (!modelId) return;
    setLoading(true); setData(null); setChoices({});
    try {
      setData(await api.getPiiConsistency(modelId));
    } catch (e) {
      toast.error(e.message || 'Could not scan for inconsistencies');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [modelId, onOpenChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const issues = data?.issues || [];
  const chosenConflicts = Object.values(choices).filter(Boolean).length;
  const canApply = !!data && ((data.auto_resolvable || 0) > 0 || chosenConflicts > 0);

  const apply = async () => {
    if (!modelId) return;
    setApplying(true);
    try {
      const r = await api.applyPiiConsistency(modelId, { category_choices: choices });
      const parts = [];
      if (r.pii_columns_unified) parts.push(`${r.pii_columns_unified} PII`);
      if (r.category_columns_unified) parts.push(`${r.category_columns_unified} category`);
      toast.success(r.columns_fixed_count
        ? `Fixed ${r.columns_fixed_count} column(s)${parts.length ? ` — unified ${parts.join(' + ')}` : ''}`
        : 'No inconsistencies to fix');
      if (r.conflicts_count) toast.warning(`${r.conflicts_count} column(s) still need a category choice`);
      onOpenChange(false);
      onApplied && onApplied(r);
    } catch (e) {
      toast.error(e.message || 'Sweep failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl" data-testid="pii-consistency-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-500" />
            Fix PII Inconsistencies{modelName ? ` — ${modelName}` : ''}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Columns that appear in more than one entity but are classified differently. Applying unifies PII (any flagged ⇒ all flagged) and fills blank data categories from a single matching value. For a category conflict, pick the winning value to resolve it.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-sm text-muted-foreground" data-testid="pii-consistency-loading">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning model…
          </div>
        ) : !data ? null : issues.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="pii-consistency-empty">
            <CheckSquare className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
            No classification inconsistencies found — every shared column is consistent.
          </div>
        ) : (
          <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1" data-testid="pii-consistency-list">
            {issues.map((iss) => (
              <div key={iss.column} className="rounded-xl border border-border/50 p-3 text-xs" data-testid={`pii-issue-${iss.column}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-semibold font-mono">{iss.column}</span>
                  {iss.dc_conflict ? (
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-500/15 text-red-500" data-testid={`pii-issue-conflict-${iss.column}`}>Category conflict</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500">Auto-fixable</span>
                  )}
                </div>
                <div className="space-y-1">
                  {iss.occurrences.map((o, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-muted-foreground">
                      <span className="truncate">{o.entity_name}</span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        <span className={o.pii ? 'text-amber-500 font-medium' : ''}>{o.pii ? 'PII' : 'not PII'}</span>
                        <span className="opacity-50">·</span>
                        <span>{o.data_category || 'no category'}</span>
                      </span>
                    </div>
                  ))}
                </div>

                {iss.resolution && (iss.resolution.pii || iss.resolution.data_category) && (
                  <div className="mt-1.5 pt-1.5 border-t border-border/40 text-[11px]">
                    <span className="text-muted-foreground">Will set: </span>
                    {iss.resolution.pii ? <span className="text-amber-500 font-medium">PII{iss.resolution.pii_type ? ` (${iss.resolution.pii_type})` : ''}</span> : null}
                    {iss.resolution.pii && iss.resolution.data_category ? <span className="opacity-50"> · </span> : null}
                    {iss.resolution.data_category ? <span>category “{iss.resolution.data_category}”</span> : null}
                    <span className="text-muted-foreground"> on all {iss.occurrences.length} entities</span>
                  </div>
                )}

                {iss.dc_conflict && (
                  <div className="mt-1.5 pt-1.5 border-t border-border/40 flex items-center gap-2 text-[11px]">
                    <span className="text-muted-foreground shrink-0">Winning category:</span>
                    <Select value={choices[iss.column] || ''} onValueChange={(v) => setChoices((p) => ({ ...p, [iss.column]: v }))}>
                      <SelectTrigger className="h-7 text-xs rounded-lg w-52" data-testid={`pii-conflict-select-${iss.column}`}>
                        <SelectValue placeholder="Choose to resolve…" />
                      </SelectTrigger>
                      <SelectContent>
                        {iss.distinct_categories.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-border/40">
          <span className="text-[11px] text-muted-foreground">
            {data ? `${(data.auto_resolvable || 0) + chosenConflicts} to fix · ${(data.conflicts || 0) - chosenConflicts} unresolved conflict(s)` : ''}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={() => onOpenChange(false)} data-testid="pii-consistency-cancel">Close</Button>
            <Button size="sm" className="rounded-lg text-xs h-7" onClick={apply}
              disabled={applying || loading || !canApply} data-testid="pii-consistency-apply">
              {applying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Apply Fixes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PiiConsistencyDialog;
